import "@mdxeditor/editor/style.css";
import { Flex, theme } from "antd";
import { useEffect, useRef } from "react";

import {
  BoldItalicUnderlineToggles,
  codeBlockPlugin,
  codeMirrorPlugin,
  CodeToggle,
  CreateLink,
  headingsPlugin,
  imagePlugin,
  InsertCodeBlock,
  InsertImage,
  InsertTable,
  linkDialogPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  quotePlugin,
  sandpackPlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { API_URL } from "../config";
import { useAuth } from "../providers";
import styled from "styled-components";

const StyledEditor = styled.div`
  & .markdown-editor {
    background: ${({ $colorBgContainer }) => $colorBgContainer} !important;
    backdrop-filter: blur(10px) !important;
    border-radius: 12px !important;
    border: 1px solid #94a3b8 !important;
    justify-self: start !important;
    text-align: left !important; // Agrega esta línea
    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    strong,
    span,
    em,
    li {
      color: ${({ $colorText }) => $colorText} !important;
      font-family: "Inter", sans-serif !important;
      justify-items: start !important;
    }
  }

  // Styling for toolbar header of text editor
  & .markdown-editor-toolbar {
    background: ${({ $colorBgContainer }) => $colorBgContainer} !important;
    backdrop-filter: blur(10px) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
  }

  & .markdown-editor-toolbar button {
    background: ${({ $colorBgContainer }) => $colorBgContainer} !important;
    backdrop-filter: blur(10px) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    svg {
      color: ${({ $colorText }) => $colorText} !important;
    }
  }

  // Style for the CodeMirror editor (code and block of code)
  & .markdown-editor code {
    span {
      background: ${({ $colorBgContainer }) => $colorBgContainer} !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
    }
  }

  .markdown-editor .cm-content {
    padding-top: 30px !important;
    color: ${({ $colorText }) => $colorText} !important;
    background-color: ${({ $colorBgContainer }) =>
      $colorBgContainer} !important;
    caret-color: ${({ $colorText }) => $colorText} !important;
  }

  .markdown-editor .cm-line {
    color: ${({ $colorText }) => $colorText} !important;
  }

  .markdown-editor .cm-activeLine {
    background-color: ${({ $colorBgElevated }) => $colorBgElevated} !important;
    color: black !important;
  }

  .markdown-editor .cm-gutters {
    background-color: #e6e6e6 !important;
    color: black !important;
    border: none !important;
  }
  // Styling for the CodeMirror toolbar (code language selector)
  ._codeMirrorToolbar_uazmk_408 {
    background: ${({ $colorBgContainer }) => $colorBgContainer} !important;
    backdrop-filter: blur(10px) !important;
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.2) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
  }

  ._selectTrigger_uazmk_307 {
    background: ${({ $colorBgContainer }) => $colorBgContainer} !important;
    backdrop-filter: blur(10px) !important;
    box-shadow: 0 20px 25px rgba(0, 0, 0, 0.2) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
  }
`;

export const MarkDownEditorComponent = ({
  placeHolder = "",
  name = "",
  onChange = null,
  onSubmit = null,
  initialValue = null,
}) => {
  const ref = useRef(null);
  const { token } = useAuth();
  const {
    token: { colorBgContainer, colorText, colorBgOverlay, colorBgElevated },
  } = theme.useToken();

  async function imageUploadHandler(image) {
    const formData = new FormData();
    formData.append("file", image);

    const response = await fetch(`${API_URL}/images/upload`, {
      method: "POST",
      body: formData,
      Authorization: `Bearer ${token}`,
    });
    if (response.ok) {
      const json = await response.json();
      return json.url;
    } else {
      throw new Error("Error al subir la imagen");
    }
  }

  useEffect(() => {
    if (initialValue && ref.current) {
      const safeValue = typeof initialValue === "string" ? initialValue : "";
      ref.current.setMarkdown(safeValue);
    }
  }, [initialValue]);

  return (
    <StyledEditor
      $colorBgContainer={colorBgContainer}
      $colorText={colorText}
      $colorBgOverlay={colorBgOverlay}
      $colorBgElevated={colorBgElevated}
    >
      <Flex gap={20} vertical>
        <MDXEditor
          ref={ref}
          markdown={typeof initialValue === "string" ? initialValue : ""}
          className="markdown-editor"
          placeholder={placeHolder}
          onChange={(markdown) => {
            if (onChange == null) {
              throw new Error("onChange prop is required");
            }

            const safeMarkdown = typeof markdown === "string" ? markdown : "";
            onChange(safeMarkdown === "" ? "" : { name, value: safeMarkdown });
          }}
          plugins={[
            toolbarPlugin({
              toolbarClassName: "markdown-editor-toolbar",
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <BoldItalicUnderlineToggles />
                  <ListsToggle />
                  <CodeToggle />
                  <InsertCodeBlock />
                  <CreateLink />
                  <InsertImage />
                  <InsertTable />
                </>
              ),
            }),
            linkDialogPlugin(),
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            imagePlugin({
              imageUploadHandler,
              imageAutocompleteSuggestions: [],
            }),
            tablePlugin({
              stringLength: 10,
              tableCellPadding: 10,
            }),
            codeBlockPlugin({ defaultCodeBlockLanguage: "python" }),
            sandpackPlugin({
              sandpackConfig: {
                defaultPreset: "react",
                presets: [
                  {
                    label: "React",
                    name: "react",
                    meta: "live react",
                    sandpackTemplate: "react",
                    sandpackTheme: "light",
                    snippetFileName: "/App.js",
                    snippetLanguage: "jsx",
                  },
                ],
              },
            }),
            codeMirrorPlugin({
              codeBlockLanguages: {
                python: "Python",
                js: "Node.js",
              },
            }),
          ]}
        />
        {onsubmit && (
          <div>
            <button onClick={() => onSubmit(ref.current?.getMarkdown())}>
              Guardar
            </button>
          </div>
        )}
      </Flex>
    </StyledEditor>
  );
};
